import { Order } from "../../../types";
import { DistanceService } from "../core/distanceService";

/**
 * Calculates the best insertion index for a new order into an existing route
 * using the Cheapest Insertion heuristic.
 * 
 * @param route The current sequence of orders for the driver.
 * @param newOrder The order being added.
 * @param hubLocation The location of the hub (start/end point).
 * @returns The index where the new order should be inserted (0 to route.length).
 */
export const calculateBestInsertion = (
    route: Order[],
    newOrder: Order,
    hubLocation: { lat: number, lng: number }
): number => {
    if (!newOrder.lat || !newOrder.lng) return route.length; // Fallback to end if no coords
    if (route.length === 0) return 0;

    let bestCost = Infinity;
    let bestIndex = route.length;

    // Helper to get coords of a node (hub or order)
    const getCoords = (index: number) => {
        if (index === -1 || index === route.length) return hubLocation;
        const o = route[index];
        return { lat: o.lat || hubLocation.lat, lng: o.lng || hubLocation.lng };
    };

    // Iterate through all possible insertion points:
    // 0: Before first order
    // i: Between order i-1 and order i
    // length: After last order
    for (let i = 0; i <= route.length; i++) {
        const prevNode = getCoords(i - 1);
        const nextNode = getCoords(i);

        // Current distance between prev and next (without newOrder)
        const currentDist = DistanceService.getDistanceSync(prevNode.lat, prevNode.lng, nextNode.lat, nextNode.lng);

        // New distance going prev -> newOrder -> next
        const distToNew = DistanceService.getDistanceSync(prevNode.lat, prevNode.lng, newOrder.lat, newOrder.lng);
        const distFromNew = DistanceService.getDistanceSync(newOrder.lat, newOrder.lng, nextNode.lat, nextNode.lng);
        const newDist = distToNew + distFromNew;

        // Marginal cost (how much extra distance this insertion adds)
        const cost = newDist - currentDist;

        if (cost < bestCost) {
            bestCost = cost;
            bestIndex = i;
        }
    }

    return bestIndex;
};
